import type { GameState, Passenger } from '@full-elevator/sim';
import { nextStops } from '@full-elevator/sim';
import { LAYOUT } from '../theme.js';

/**
 * What the cabin shows, and how big (v0.8, KNOWN_ISSUES "riders' numbers are
 * unreadable on tall buildings").
 *
 * It used to draw one ticket per rider on a grid sized for every seat, so a
 * six-seat cabin on a 12-floor level split 52px into three rows and printed
 * each destination at 6px — 5px for a `6›9` transfer. But a player in the
 * cabin decides by *stop*, not by rider: which floors does this car still owe?
 * So it now draws one ticket per stop, with the riders for it stacked behind,
 * on a grid sized for the stops actually owed. Fewer tickets, each bigger.
 */

/** The cabin's box for a floor this tall and a car this big. Bigger cars are drawn bigger. */
export function cabinSize(
  floorHeight: number,
  seats: number,
  shaftWidth: number = LAYOUT.shaftWidth,
): { width: number; height: number } {
  return {
    width: shaftWidth - 22 + Math.min(seats, 6),
    height: Math.min(floorHeight - 8, 62 + seats * 5),
  };
}

/** Inside the cabin's walls, around the grid, and between tickets. */
export const CABIN_PAD = 4;
export const CABIN_GAP = 3;
/** A stop ticket never grows past a waiting ticket. */
export const STOP_MAX = 28;

export interface RiderGrid {
  readonly columns: number;
  readonly rows: number;
  /** A stop ticket: wider than tall when the cabin is short, so a two-digit floor still fits. */
  readonly width: number;
  readonly height: number;
  /** The number on it. */
  readonly font: number;
}

/**
 * The number on a stop ticket. A stop ticket carries one number, never a
 * transfer's two — the riders get out at that stop either way — so it can use
 * most of the ticket, and it never drops below what a phone can be read at.
 */
export const STOP_FONT_MIN = 11;

/** The largest number a ticket this size holds: two digits across, a little air above and below. */
function fontFor(width: number, height: number): number {
  return Math.floor(Math.min(height - 3, (width - 4) / 1.1, 17));
}

/**
 * The arrangement that makes the numbers on `count` stop tickets as large as
 * the cabin allows: tried at every column count, the biggest number wins.
 */
export function riderGrid(count: number, width: number, height: number): RiderGrid {
  let best: RiderGrid = { columns: 1, rows: 1, width: 0, height: 0, font: 0 };
  for (let columns = 1; columns <= Math.max(1, count); columns += 1) {
    const rows = Math.ceil(count / columns);
    const cellW = (width - CABIN_PAD * 2 - (columns - 1) * CABIN_GAP) / columns;
    const cellH = (height - CABIN_PAD * 2 - (rows - 1) * CABIN_GAP) / rows;
    const h = Math.floor(Math.min(cellH, STOP_MAX));
    const w = Math.floor(Math.min(cellW, h * 1.5, STOP_MAX * 1.2));
    const font = fontFor(w, h);
    if (font > best.font) best = { columns, rows, width: w, height: h, font };
  }
  return best;
}

export interface StopGroup {
  /** Where they get out next: their floor, or the transfer floor across an interchange. */
  readonly stop: number;
  readonly riders: readonly Passenger[];
}

/**
 * The riders by the stop they owe, highest floor first — the way the building
 * reads. In a bank of cars (ADR-0034), only the riders of `aboard`.
 */
export function stopsOwed(state: GameState, aboard?: readonly string[]): StopGroup[] {
  const byStop = new Map<number, Passenger[]>();
  for (const passenger of state.passengers) {
    if (passenger.state !== 'riding') continue;
    if (aboard && !aboard.includes(passenger.id)) continue;
    // Where the car is worth taking them now: a postman's round first, and his
    // own floor only once it is done (ADR-0031). The cabin showed his 8 with 6
    // still owed, and the owner stood at 8 for 14 s waiting for him to get off
    // (PLAYTEST_LOG, 2026-10-28).
    for (const stop of nextStops(state, passenger)) {
      const list = byStop.get(stop);
      if (list) list.push(passenger);
      else byStop.set(stop, [passenger]);
    }
  }
  return [...byStop.entries()]
    .sort(([a], [b]) => b - a)
    .map(([stop, riders]) => ({ stop, riders }));
}

/** What the cabin can show at a readable size: some of the stops it owes, and how many more. */
export interface ReadableStops {
  readonly shown: readonly StopGroup[];
  /** Stops owed but not drawn, shown as one "+N" ticket after the rest. */
  readonly more: number;
  readonly grid: RiderGrid;
}

/**
 * Every stop a cabin owes, as long as each number stays readable — and when a
 * big car on a tall building owes more stops than that (a ten- or twelve-seat
 * car on twelve floors, ADR-0027), the nearest of them, with the rest folded
 * into one "+N" ticket. The nearest because they are what the next choice is
 * about; still highest first, the way the building reads. Never a number under
 * `STOP_FONT_MIN`.
 */
export function readableStops(
  stops: readonly StopGroup[],
  position: number,
  width: number,
  height: number,
): ReadableStops {
  const all = riderGrid(stops.length, width, height);
  if (all.font >= STOP_FONT_MIN || stops.length <= 1) return { shown: stops, more: 0, grid: all };
  for (let n = stops.length - 1; n >= 1; n -= 1) {
    const grid = riderGrid(n + 1, width, height);
    if (grid.font < STOP_FONT_MIN) continue;
    const nearest = [...stops]
      .sort((a, b) => Math.abs(a.stop - position) - Math.abs(b.stop - position) || b.stop - a.stop)
      .slice(0, n)
      .sort((a, b) => b.stop - a.stop);
    return { shown: nearest, more: stops.length - n, grid };
  }
  const grid = riderGrid(2, width, height);
  return { shown: stops.slice(0, 1), more: stops.length - 1, grid };
}
